import { BlogCard } from './types';

export const walkInUrgentCareBlogCards: BlogCard[] = [
  {
    title: {
      content:
        '### Walk-in urgent care clinic in London: same-day private assessment at Battersea Power Station',
      styles: {
        h3: 'font-Mulish font-bold text-royal-875 my-3 text-base md:text-lg leading-tight line-clamp-2',
      },
      topicsStyles: {
        h3: 'text-theme-primary text-3xl md:text-4xl my-4 md:my-6 p-2 font-black',
      },
    },
    description: {
      content:
        'Need a walk-in urgent care clinic in London? LIPS Healthcare at Battersea Power Station offers same-day private assessment. No referral needed. Book now.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    metaTitle: 'Walk-In Urgent Care Clinic London | LIPS Healthcare',
    imageUrl: 'no-waiting-list.webp',
    bannerUrl: 'ae-waiting-times-banner.webp',
    bannerDescription:
      'A walk-in urgent care clinic in London offers same-day private assessment for injuries and illnesses without a pre-booked appointment, an NHS referral, or a long A&E queue. LIPS Urgent Care Centre at Turbine Hall B, Battersea Power Station, accepts walk-in patients seven days a week alongside pre-bookable slots, led by GMC-registered Consultants in Emergency Medicine. Book in advance or walk in directly at [lips.org.uk/specialities/urgent-care-centre](/specialities/urgent-care-centre). This guide explains how a private walk-in urgent care clinic in London works, when to use one, and what to expect.',
    slug: 'walk-in-urgent-care-clinic-london',
    contentType: 'article',
    speciality: 'Urgent Care Clinic',
    date: {
      content: '24, September 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    author: {
      content: 'LIPS Healthcare Marketing Team',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    topics: {
      title: {
        content: '#### TOPICS:',
        styles: {
          h4: 'text-theme-primary font-extrabold p-2',
        },
      },
      topics: [
        'Walk-In Urgent Care',
        'Urgent Care Clinic',
        'Walk-In Clinic London',
        'Battersea Power Station',
        'Same-Day Care',
      ],
      styles: {
        p: 'p-2 bg-info-500-opac-10 text-teal-450 bg-info-500-soft',
      },
    },
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'What is a walk-in urgent care clinic?',
          id: 'what-is-a-walk-in-urgent-care-clinic',
        },
        {
          text: 'When should you use a walk-in urgent care clinic rather than your GP or A&E?',
          id: 'when-to-use-walk-in-urgent-care',
        },
        {
          text: 'What happens when you walk in to LIPS Urgent Care Centre?',
          id: 'what-happens-when-you-walk-in',
        },
        {
          text: 'Walk-in urgent care clinic versus NHS options: what is the difference?',
          id: 'walk-in-vs-nhs-options',
        },
        {
          text: 'How much does a walk-in urgent care clinic cost in London?',
          id: 'how-much-does-it-cost',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    content: {
      content: `> ⓘ If you are experiencing a life-threatening emergency, including loss of consciousness, severe chest pain, difficulty breathing, signs of stroke, or major trauma: call 999 or attend your nearest NHS Emergency Department immediately. Do not use a walk-in urgent care clinic for these conditions.

<span id="what-is-a-walk-in-urgent-care-clinic"></span>

### What is a walk-in urgent care clinic?

A walk-in urgent care clinic is a private medical facility where you can present on the same day, without a pre-booked appointment, for assessment and treatment of urgent but non-life-threatening conditions. As a private walk in clinic London patients can attend without an NHS referral, LIPS Urgent Care Centre at Battersea Power Station operates seven days a week alongside a bookable appointment model, giving patients the flexibility to attend at short notice while still having the option to secure a confirmed time in advance.

If you are searching for walk in care near me across south or central London, LIPS Urgent Care Centre at Battersea Power Station is the private consultant-led option accessible from Chelsea, Clapham, Vauxhall, Pimlico, Wandsworth, and Nine Elms in under 15 minutes via the Northern line, London Overground, or bus.

Conditions assessed at LIPS Urgent Care Centre include:

- *Injuries:* sprains, suspected fractures, cuts requiring closure, and wound infections.
- *Infections and acute illness:* fever, chest infections, urinary tract infections, and acute illness requiring same-day review.
- *Severe headaches:* sudden or severe headaches requiring senior clinical assessment.
- *Chest and breathing symptoms:* chest pain and breathlessness in patients under 50, requiring prompt evaluation.
- *Abdominal symptoms:* pelvic pain, bowel symptoms, and urinary retention in patients under 50.
- *Pain that cannot wait:* any significant acute pain that requires assessment the same day.

<strong><a href="/specialities/urgent-care-centre#booking-calendar-widget" target="_blank" rel="noopener noreferrer">Walk in or book same-day at LIPS Urgent Care Centre</a></strong>

<span id="when-to-use-walk-in-urgent-care"></span>

### When should you use a walk-in urgent care clinic rather than your GP or A&E?

A private walk-in urgent care clinic in London is the right choice when you have a sudden illness or injury that cannot wait for a routine GP appointment but does not require a 999 emergency response. The decision between a walk-in clinic, your GP, and A&E comes down to three factors: the urgency of your condition, the seniority of clinician you need, and whether same-day investigations are required.

Use a walk-in urgent care clinic when:

- Your GP cannot offer a same-day appointment and your condition cannot wait two or more days.
- You have an acute injury or illness that needs examination, investigation, or treatment on the same day.
- You need access to on-site X-ray, blood tests, or ultrasound without being referred elsewhere.
- Your condition is urgent but does not warrant calling 999 or attending A&E.
- You want to be seen by a senior clinician, not a triage nurse or duty GP.

Your GP remains the best first point of contact for ongoing conditions, repeat prescriptions, medication reviews, and non-urgent health concerns. A&E is right for immediately life-threatening emergencies. LIPS Urgent Care Centre at Battersea Power Station sits between the two: same-day consultant-led assessment for conditions that are urgent but not life-threatening, serving patients from Chelsea, Clapham, Pimlico, and Wandsworth who need a faster and more senior clinical response than routine primary care can offer.

<span id="what-happens-when-you-walk-in"></span>

### What happens when you walk in to LIPS Urgent Care Centre?

The walk-in process at LIPS Urgent Care Centre, Turbine Hall B, Battersea Power Station, is straightforward: no triage queue, no requirement to call ahead, and no referral needed. A GMC-registered Consultant in Emergency Medicine takes responsibility for your assessment from arrival to discharge.

- *Registration:* you provide your details at reception. Adults should bring photo identification. Paediatric patients must be accompanied by a parent or legal guardian with proof of parental responsibility.
- *Assessment:* your consultant takes a full clinical history, examines the affected area, and establishes a working diagnosis.
- *Investigations:* where clinically appropriate, X-ray, blood tests, ECG, or ultrasound are arranged on-site, on the same day, with results reviewed by your consultant before discharge.
- *Treatment:* wound closure, splinting, dressings, IV fluids, or medication as required. All additional costs are discussed and agreed before any treatment is carried out.
- *Discharge:* you leave with a written discharge summary, management plan, safety-netting advice, and referral to a specialist if further care is required.

The entire visit, including any same-day investigations, is typically completed within a single appointment. You will not be sent to a separate facility for X-ray or blood tests. Everything is available at Battersea Power Station.

<strong><a href="/specialities/urgent-care-centre#booking-calendar-widget" target="_blank" rel="noopener noreferrer">Walk in or book same-day at LIPS Urgent Care Centre</a></strong>

<span id="walk-in-vs-nhs-options"></span>

### Walk-in urgent care clinic versus NHS options: what is the difference?

Private walk-in urgent care clinics differ from NHS walk-in centres and GP surgeries in clinician seniority, investigation access, and appointment certainty. The table below sets out the key differences for patients searching for a walk in clinic London-wide who are weighing up NHS and private options.

| | NHS walk-in / GP | LIPS Walk-In (Battersea) |
|---|---|---|
| Booking | Walk-in, no prior booking | Walk in or book in advance |
| Clinician | Nurse, GP, or junior doctor | GMC-registered Consultant in Emergency Medicine |
| Wait time | Unpredictable; often 1 to 4 hours | Confirmed appointment or short walk-in wait |
| Same-day X-ray | Rarely available at NHS walk-in | Available on-site at Battersea Power Station |
| Referral needed | No | No |
| Cost | Free on NHS | From £150 (see below) |

LIPS Urgent Care Centre is regulated by the Care Quality Commission (CQC) and is located on the first floor of Turbine Hall B, Battersea Power Station, London. The clinic is reachable from Chelsea, Clapham, Vauxhall, Pimlico, Wandsworth, and Nine Elms via the Northern line to Battersea Power Station station, London Overground, or direct bus routes.

<span id="how-much-does-it-cost"></span>

### How much does a walk-in urgent care clinic cost in London?

The consultation fee at LIPS Urgent Care Centre is £180 for weekday appointments and £200 at weekends. This covers a full clinical assessment by a GMC-registered Consultant in Emergency Medicine, including examination, observations, a management plan, a written discharge summary, and safety advice.

Additional investigations and treatments are arranged where clinically appropriate and discussed with you before any cost is incurred. Common add-on costs include:

- *Blood tests:* included in condition-specific packages such as the Infection and Acute Illness Package (£360 total) and the Generally Unwell Package (£380 total).
- *X-ray (single site):* included in the Injury and Musculoskeletal Package (£315 total). Additional X-ray sites cost £75 each.
- *ECG:* included in the Chest Pain and Breathing Package (£545 total).
- *IV fluids or medication:* £80 per session.
- *Wound closure by suturing:* £135.

Receipts are issued for all treatments and can be submitted for reimbursement through private medical or travel insurance where policies cover urgent care. Patients attending from Chelsea, Clapham, Vauxhall, or Pimlico can confirm current pricing and package details before attending by calling +44 20 3870 9999 or visiting [lips.org.uk/specialities/urgent-care-centre](/specialities/urgent-care-centre).`,
      styles: {
        h3: 'text-lg md:text-xl font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        ul: 'list-disc pl-6 space-y-2 mb-4',
        li: 'text-text-default leading-relaxed',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        blockquote:
          'border-l-4 border-theme-primary bg-blue-50 border border-blue-200 rounded-lg p-4 my-8 not-italic [&_p]:text-sm [&_p]:text-royal-900 [&_p]:font-medium [&_p]:mb-0',
        table:
          'w-full table-fixed border-collapse text-xs md:text-sm border-2 border-black',
        th: 'border-2 border-black bg-text-muted-blue px-2 py-2 md:px-4 md:py-3 text-left font-semibold text-theme-primary whitespace-normal wrap-break-word',
        td: 'border-2 border-black px-2 py-2 md:px-4 md:py-3 align-top font-semibold whitespace-normal wrap-break-word',
        em: 'text-royal-900 font-bold not-italic',
      },
      tableOptions: {
        width: '100%',
        columnWidths: ['20%', '40%', '40%'],
      },
    },
    faq: {
      heading: {
        content: '#### Frequently asked questions (FAQS)',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'Is urgent care the same as a walk-in centre?',
          },
          answer: {
            content:
              'Not exactly. A walk-in centre is usually an NHS primary care facility staffed by nurses or GPs treating minor conditions without an appointment. An urgent care clinic such as LIPS Urgent Care Centre at Battersea Power Station is a private, CQC-regulated clinic staffed by GMC-registered Consultants in Emergency Medicine, with access to on-site X-ray, blood tests, and imaging at the same location. Both accept walk-in patients without a referral, but the clinical seniority and investigation capability differ significantly.',
          },
        },
        {
          question: {
            content:
              'Do I need an appointment for a walk-in urgent care clinic?',
          },
          answer: {
            content:
              'No. LIPS Urgent Care Centre at Battersea Power Station accepts walk-in patients seven days a week without a prior appointment. Pre-booking a slot is available and recommended for paediatric patients or those who want a confirmed appointment time, but walk-in access is always available subject to clinical capacity.',
          },
        },
        {
          question: {
            content: 'How does urgent care work in London?',
          },
          answer: {
            content:
              'Private urgent care in London works by providing same-day consultant-led assessment for conditions that are urgent but not life-threatening. At LIPS Urgent Care Centre, Battersea Power Station, you can walk in or book online, are assessed by a GMC-registered Consultant in Emergency Medicine, and receive examination, investigation, and treatment in a single visit. There is no NHS referral requirement and no waiting list.',
          },
        },
        {
          question: {
            content: 'Can I just walk into a GP for urgent care?',
          },
          answer: {
            content:
              'No. NHS GP surgeries operate appointment-based systems and same-day slots are limited and not guaranteed. For urgent conditions requiring immediate clinical assessment on the same day, a private walk-in urgent care clinic in London such as LIPS Urgent Care Centre at Battersea Power Station offers a more reliable same-day access route, with a senior consultant leading the assessment from arrival.',
          },
        },
        {
          question: {
            content:
              'How do I find a walk-in urgent care clinic near me open now?',
          },
          answer: {
            content:
              'LIPS Urgent Care Centre is open Monday to Friday from 9:00 am to 9:00 pm, Saturday from 10:00 am to 7:00 pm, and Sunday from 12:00 pm to 6:00 pm. The clinic is on the first floor of Turbine Hall B, Battersea Power Station, and is accessible from Chelsea, Clapham, Vauxhall, Pimlico, Wandsworth, and Nine Elms via the Northern line, London Overground, or bus. Call +44 20 3870 9999 to confirm availability before attending.',
          },
        },
      ],
    },
    postFaqContent: {
      content:
        '<strong><a href="/specialities/urgent-care-centre#booking-calendar-widget" target="_blank" rel="noopener noreferrer">Private walk-in clinic. No queue. Book now.</a></strong>\n\n>ⓘ Medical disclaimer: This article is for informational purposes only and does not constitute medical advice, diagnosis, or treatment. Always seek the advice of a qualified clinician for any medical condition.\n\nSource: [NHS guidance on urgent care and walk-in centres](https://www.nhs.uk/).',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: ' text-text-action font-bold ',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 rounded-lg font-bold no-underline cursor-pointer mt-8 py-3 px-6 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        blockquote:
          'border-l-4 border-theme-primary bg-surface-secondary border border-blue-200 rounded-lg p-4 my-8 text-sm text-text-default not-italic',
      },
    },
  },
];
